import React, {useEffect, useState} from "react";
import {useHistory, useParams} from "react-router-dom";
import {selectToken} from "../../../features/account/accountSlice";
import {useAbortController, useAccessInfo, useAppDispatch, useAppSelector} from "../../../hooks";
import ErrorResponse from "../../../models/ErrorResponse";
import {Record} from '../../../models/Record';
import * as ApiHelper from '../../../api/ApiHelper';
import {formatDateTime} from "../../../helpers/FormatHelper";
import AsyncIndicator from "../../Widgets/AsyncIndicator/AsyncIndicator";
import {FetchError} from "../../Widgets/FetchError/FetchError";
import handleErrors from "../../../helpers/ErrorHandler";
import {Role} from "../../../models/Role";
import {Breadcrumbs} from "../../Widgets/Breadcrumbs/Breadcrumbs";
import {Button} from "../../Widgets/Button/Button";
import {DialogConfirmation} from "../../Widgets/DialogConfirmation/DialogConfirmation";
import {Toast} from "../../Widgets/Toast/Toast";
import * as ErrorCodes from "../../../api/ErrorCodes";
import NotFound from "../../Widgets/NotFound/NotFound";
import {downloadFile} from "../../../helpers/DownloadHelper";
import {useTranslation} from "react-i18next";
import {RecordDetailsTable} from "../../Widgets/RecordDetailsTable/RecordDetailsTable";

interface PathParams {
    recordId: string;
}

interface RecordDetailsProps {
    canShare: boolean;
}

export const RecordDetails: React.FC<RecordDetailsProps> = ({canShare}: RecordDetailsProps) => {
    const {t} = useTranslation();
    let {recordId} = useParams<PathParams>();
    const controller = useAbortController();
    const history = useHistory();
    const dispatch = useAppDispatch();
    const token = useAppSelector(selectToken);
    const isSupport = useAccessInfo(Role.Support);
    const [isFetching, setFetchingState] = useState(true);
    const [hasError, setErrorState] = useState(false);
    const [notFound, setNotFoundState] = useState(false);
    const [hasDeleteError, setDeleteErrorState] = useState(false);
    const [hasDownloadError, setDownloadErrorState] = useState(false);
    const [record, setRecord] = useState(null as Record | null);
    const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState(false);
    const recordHandler = (record: Record) => {
        setFetchingState(false);
        setErrorState(false);
        setNotFoundState(false);
        setRecord(record);
    };
    const deleteHandler = () => {
        setErrorState(false);
        setNotFoundState(false);
        setDeleteErrorState(false);
        setFetchingState(false);
        history.goBack();
    }
    const errorHandler = (error: ErrorResponse) => {
        setFetchingState(false);
        if (!handleErrors(error, dispatch)) {
            if (error.ResultCode === ErrorCodes.NotFound) {
                setNotFoundState(true);
                setErrorState(false);
            } else {
                setErrorState(true);
                setNotFoundState(false);
            }
        }
    };
    const deleteErrorHandler = (error: ErrorResponse) => {
        if (!handleErrors(error, dispatch)) {
            setDeleteErrorState(false);
            setDeleteErrorState(true);
        }
        setFetchingState(false);
    };
    const downloadErrorHandler = (error: ErrorResponse) => {
        if (!handleErrors(error, dispatch)) {
            setDownloadErrorState(false);
            setDownloadErrorState(true);
        }
    };
    const fetchRecord = () => {
        let userToken = token?.token;
        if (userToken) {
            setFetchingState(true);
            ApiHelper.getRecord(userToken, recordId, controller, recordHandler, errorHandler);
        } else {
            setErrorState(true);
        }
    };
    useEffect(() => fetchRecord(), [recordId]);     // eslint-disable-line
    const deleteRecord = () => {
        setShowDeleteConfirmationDialog(false);
        let userToken = token?.token;
        if (userToken) {
            setFetchingState(true);
            ApiHelper.deleteRecord(userToken, recordId, controller, deleteHandler, deleteErrorHandler)
        }
    };
    const handleViewEcgClick = () => {
        if (record && record.ecgStudy && record.ecgStudy.ecgId) {
            history.push(`${history.location.pathname}/ecg`);
        }
    };
    const handleViewSpiroClick = () => {
        if (record && record.spiroStudy && record.spiroStudy.spiroId) {
            history.push(`${history.location.pathname}/spiro`);
        }
    };
    const handleViewStethoscopeClick = () => {
        if (record && record.stethoscopeStudy && record.stethoscopeStudy.stethoscopeId) {
            history.push(`${history.location.pathname}/stethoscope`);
        }
    };
    const downloadStudy = (studyId: string | undefined) => {
        let userToken = token?.token;
        if (userToken && record && studyId) {
            ApiHelper.getZipStudy(userToken, record.id, studyId, controller, () => {
            }, downloadErrorHandler);
        }
    }
    const handleDownloadEcgClick = () => {
        downloadStudy(record?.ecgStudy?.ecgId);
    };
    const handleDownloadSpiroClick = () => {
        downloadStudy(record?.spiroStudy?.spiroId);
    };
    const handleDownloadStethoscopeClick = () => {
        downloadStudy(record?.stethoscopeStudy?.stethoscopeId);
    };
    const handleDownloadLtEcgClick = () => {
        const link = record?.ltEcgStudy?.recordLink;
        if (link) {
            downloadFile(link, null);
        }
    };
    const accessControlHandler = () => {
        history.push(`${history.location.pathname}/share`);
    }
    const isOk = !hasError && !notFound && !isFetching;
    return (
        <div>
            <Breadcrumbs
                data={new Map([[recordId, record ? formatDateTime(record.dateTime) : null], [record?.patientId ?? "_", record?.patientName ?? null]])}/>
            {isFetching && <AsyncIndicator/>}
            {!isFetching && hasError && <FetchError onRetry={fetchRecord}/>}
            {!isFetching && notFound && <NotFound/>}
            {(isOk && record) &&
            <RecordDetailsTable record={record} isSupport={isSupport}
                                handleViewEcgClick={handleViewEcgClick}
                                handleDownloadEcgClick={handleDownloadEcgClick}
                                handleViewSpiroClick={handleViewSpiroClick}
                                handleDownloadSpiroClick={handleDownloadSpiroClick}
                                handleViewStethoscopeClick={handleViewStethoscopeClick}
                                handleDownloadStethoscopeClick={handleDownloadStethoscopeClick}
                                handleDownloadLtEcgClick={handleDownloadLtEcgClick}/>
            }
            {(isOk && record && canShare) &&
            <div className="d-flex justify-content-center">
                <Button className="mx-2" text={t("access_control")} onClick={accessControlHandler}/>
            </div>
            }
            {(isOk && record && isSupport) &&
            <div className="d-flex justify-content-center">
                <div className="details-controls d-flex justify-content-around my-2">
                    <Button text={t("delete")} danger={true} onClick={() => {
                        setShowDeleteConfirmationDialog(true)
                    }}/>
                </div>
            </div>
            }
            {showDeleteConfirmationDialog &&
            <DialogConfirmation titleText={t("delete_record_title")} messageText={t("delete_record_description")}
                                okButtonText={t("delete")} cancelButtonText={t("cancel")}
                                okButtonClickHandler={deleteRecord}
                                cancelButtonClickHandler={() => setShowDeleteConfirmationDialog(false)}/>}
            {hasDeleteError &&
            <Toast text={t("connection_problem")} isError={true}/>
            }
            {hasDownloadError &&
            <Toast text={t("error_downloading_data")} isError={true}/>
            }
        </div>
    );
};

export default RecordDetails;